import React, { useState, useMemo } from 'react';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
const cmag = (c) => Math.sqrt(c.re*c.re + c.im*c.im);
const cang = (c) => Math.atan2(c.im, c.re) * RAD;
const fromPolar = (mag, angDeg) => ({ re: mag*Math.cos(angDeg*DEG), im: mag*Math.sin(angDeg*DEG) });
const cmul = (a, b) => ({ re: a.re*b.re - a.im*b.im, im: a.re*b.im + a.im*b.re });

const normAng = d => {
  let n = ((d % 360) + 360) % 360;
  if (n > 180) n -= 360;
  return n;
};

// Unit circle SVG with 4 phasors: V, aV, a²V, a³V
const ACircleSVG = ({ vmag, vang, dark }) => {
  const CX = 140, CY = 140, R = 105;
  const bg = dark ? '#0f172a' : '#ffffff';
  const ax = dark ? '#1e293b' : '#e2e8f0';
  const lc = dark ? '#475569' : '#94a3b8';

  const V    = fromPolar(vmag, vang);
  const aV   = cmul(fromPolar(1, 120), V);
  const a2V  = cmul(fromPolar(1, 240), V);
  // a³V = original V

  const phasors = [
    { vec: V,   color: '#7c3aed', label: 'V',   ang: vang,        note: 'original' },
    { vec: aV,  color: '#0d9488', label: 'a·V', ang: vang + 120,  note: '+120°' },
    { vec: a2V, color: '#ea580c', label: 'a²·V',ang: vang + 240,  note: '+240°' },
    { vec: V,   color: '#94a3b8', label: 'a³·V',ang: vang + 360,  note: '+360°=V', dashed: true },
  ];

  const drawP = ({ vec, color, label, ang, dashed }) => {
    const mag   = Math.min(cmag(vec), 2.2);
    const rad   = Math.atan2(vec.im, vec.re);
    const sc    = R / 1.15;
    const tx    = CX + mag*sc*Math.cos(rad);
    const ty    = CY - mag*sc*Math.sin(rad);
    const bx    = CX + (mag*sc - 12)*Math.cos(rad);
    const by    = CY - (mag*sc - 12)*Math.sin(rad);
    const hw    = 5;
    const pts   = `${tx},${ty} ${bx+hw*Math.sin(rad)},${by+hw*Math.cos(rad)} ${bx-hw*Math.sin(rad)},${by-hw*Math.cos(rad)}`;
    const lx    = tx + 20*Math.cos(rad);
    const ly    = ty - 20*Math.sin(rad) + 4;
    return (
      <g key={label}>
        <line x1={CX} y1={CY} x2={bx} y2={by} stroke={color} strokeWidth={dashed ? '1.5' : '2.5'}
          strokeLinecap="round" strokeDasharray={dashed ? '5 3' : undefined} />
        <polygon points={pts} fill={color} />
        <text x={lx} y={ly} fontSize="10.5" fontWeight="700" fontFamily="IBM Plex Mono" fill={color} textAnchor="middle">{label}</text>
        <text x={lx} y={ly+12} fontSize="8" fontFamily="IBM Plex Mono" fill={color} textAnchor="middle">
          ∠{normAng(ang).toFixed(1)}°
        </text>
      </g>
    );
  };

  // 120° arc annotations between adjacent phasors
  const arcPath = (fromDeg, toDeg, r) => {
    const f = fromPolar(r, fromDeg);
    const t = fromPolar(r, toDeg);
    // In SVG coords: y is flipped
    const fx = CX + f.re, fy = CY - f.im;
    const tx = CX + t.re, ty = CY - t.im;
    return `M ${fx} ${fy} A ${r} ${r} 0 0 1 ${tx} ${ty}`;
  };

  return (
    <svg viewBox="0 0 280 280" className="w-full max-w-[260px] mx-auto block">
      <rect width="280" height="280" fill={bg} rx="10" />
      {/* Unit circle (1.0 pu) */}
      <circle cx={CX} cy={CY} r={R/1.15} fill="none" stroke={ax} strokeWidth="1" strokeDasharray="4 4" />
      {/* Axes */}
      <line x1={CX-R-8} y1={CY} x2={CX+R+8} y2={CY} stroke={ax} strokeWidth="0.8" />
      <line x1={CX} y1={CY-R-8} x2={CX} y2={CY+R+8} stroke={ax} strokeWidth="0.8" />
      <text x={CX+R+4} y={CY-3} fontSize="8.5" fill={lc} fontFamily="IBM Plex Mono">+Re</text>
      <text x={CX+3} y={CY-R-2} fontSize="8.5" fill={lc} fontFamily="IBM Plex Mono">+Im</text>
      {/* 120° arc annotations */}
      <path d={arcPath(vang, vang+120, 28)} fill="none" stroke="#0d9488" strokeWidth="1.2" />
      <text x={CX + 38*Math.cos((vang+60)*DEG)} y={CY - 38*Math.sin((vang+60)*DEG) + 4}
        fontSize="8" fill="#0d9488" fontFamily="IBM Plex Mono" textAnchor="middle">+120°</text>
      <path d={arcPath(vang+120, vang+240, 28)} fill="none" stroke="#ea580c" strokeWidth="1.2" />
      <text x={CX + 38*Math.cos((vang+180)*DEG)} y={CY - 38*Math.sin((vang+180)*DEG) + 4}
        fontSize="8" fill="#ea580c" fontFamily="IBM Plex Mono" textAnchor="middle">+120°</text>
      {/* Phasors */}
      {phasors.map(drawP)}
      <circle cx={CX} cy={CY} r="3.5" fill={dark ? '#475569' : '#94a3b8'} />
    </svg>
  );
};

const AOperatorExplorer = () => {
  const { dark } = useTheme();
  const [vmag, setVmag] = useState(1.0);
  const [vang, setVang] = useState(30);

  const V    = useMemo(() => fromPolar(vmag, vang), [vmag, vang]);
  const aV   = useMemo(() => cmul(fromPolar(1, 120), V), [V]);
  const a2V  = useMemo(() => cmul(fromPolar(1, 240), V), [V]);

  const numCls = 'w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none font-mono';

  return (
    <div data-testid="a-operator-explorer" className="space-y-5">
      {/* Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">|V| — Phasor magnitude (pu)</label>
            <TeachingTooltip showIcon
              whatThis="The magnitude of the input phasor V. In power systems, this is typically 1.0 pu for voltage (rated). The a-operator rotates without changing magnitude."
              tryThis="Leave at 1.0 pu — the a-operator is purely a rotation operator. All results stay on the unit circle."
              whyMatters="The a-operator a = 1∠120° has unit magnitude (|a| = 1). Multiplying by a rotates a phasor without scaling it."
              connectedTo="→ Sets the radius of all three output phasors (a·V, a²·V all have the same magnitude as V)." />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="v-mag-a-input" type="range" min="0.1" max="2" step="0.01" value={vmag}
              onChange={e => setVmag(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={vmag} step="0.01" min="0.1" max="2"
              onChange={e => setVmag(Math.max(0.1, Math.min(2, Number(e.target.value))))} className={numCls} />
            <span className="text-xs text-slate-500 w-7">pu</span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">∠V — Phasor angle (°)</label>
            <TeachingTooltip showIcon
              whatThis="The phase angle of the input phasor V in degrees, measured counterclockwise from the positive real axis."
              tryThis="Set ∠V = 0°, then notice a·V is at 120°, a²·V is at 240°, a³·V is back at 0°. This is the complete a-operator cycle."
              whyMatters="The a-operator adds exactly 120° to any phasor. Applying it three times returns the original: a³ = 1∠360° = 1∠0°."
              connectedTo="→ Rotates the entire set of V, a·V, a²·V simultaneously while maintaining their 120° spacing." />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="v-ang-a-input" type="range" min="-180" max="180" step="1" value={vang}
              onChange={e => setVang(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={vang} step="1" min="-180" max="180"
              onChange={e => setVang(Math.max(-180, Math.min(180, Number(e.target.value))))} className={numCls} />
            <span className="text-xs text-slate-500 w-4">°</span>
          </div>
        </div>
      </div>

      {/* Circle diagram + table */}
      <div className="flex flex-col sm:flex-row gap-6 items-start">
        <div className="w-full sm:w-auto">
          <p className="text-[10px] font-mono text-slate-500 dark:text-slate-500 mb-1.5 text-center">
            Unit circle — effect of a-operator
          </p>
          <ACircleSVG vmag={vmag} vang={vang} dark={dark} />
        </div>

        <div className="flex-1 w-full space-y-3">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest">Results</p>
          <div className="overflow-x-auto">
            <table data-testid="a-operator-table" className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500">Expression</th>
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500">Polar (pu ∠ °)</th>
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500">Rectangular</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {[
                  { expr: 'V',    vec: V,   color: '#7c3aed', op: '(original)' },
                  { expr: 'a·V',  vec: aV,  color: '#0d9488', op: '×1∠120°' },
                  { expr: 'a²·V', vec: a2V, color: '#ea580c', op: '×1∠240°' },
                  { expr: 'a³·V', vec: V,   color: '#94a3b8', op: '=V (×1∠360°)' },
                ].map(({ expr, vec, color, op }) => (
                  <tr key={expr} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="py-1.5 px-2">
                      <span className="font-mono font-bold" style={{ color }}>{expr}</span>
                      <span className="text-slate-400 dark:text-slate-600 ml-1 text-[10px]">{op}</span>
                    </td>
                    <td className="py-1.5 px-2 font-mono text-slate-700 dark:text-slate-300">
                      {cmag(vec).toFixed(4)} ∠{normAng(cang(vec)).toFixed(2)}°
                    </td>
                    <td className="py-1.5 px-2 font-mono text-slate-700 dark:text-slate-300">
                      {vec.re.toFixed(4)} {vec.im >= 0 ? '+' : '−'} j{Math.abs(vec.im).toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800 rounded-md p-3">
            <p className="text-xs text-indigo-800 dark:text-indigo-300 leading-relaxed">
              <span className="font-semibold">Key identity: </span>
              a = 1∠120° = −½ + j(√3/2) ≈ −0.5 + j0.8660.
              Therefore 1 + a + a² = 0 (phasors at 0°, 120°, 240° sum to zero — this is the balanced condition).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AOperatorExplorer;
